<script setup lang="ts">
import { computed } from 'vue'

import type { ReportColumn } from '../../../models/column.types'
import type { ReportRow } from '../../../models/report.types'
import { readPercentileBand, type PercentileBand } from '../../../service/structuredCells'

import { useI18n } from '@/shared/i18n'
import { useFormat } from '@/shared/lib/format'

const props = defineProps<{
  row: ReportRow
  column: ReportColumn
}>()

/** v3 `getBenchmarkCategoryStyle` colors (`OrphexTd.vue:432-440`), as this app's tone tokens. */
const BAND_CLASSES: Record<PercentileBand, string> = {
  poor: 'text-danger bg-danger-soft',
  belowAvg: 'text-accent bg-accent-soft',
  average: 'text-warning bg-warning-soft',
  good: 'text-success bg-success-soft',
  excellent: 'text-primary-fg bg-success',
}

const { t } = useI18n()
const format = useFormat()
const rawValue = computed(() => props.row[props.column.field])
const band = computed(() => readPercentileBand(rawValue.value))
const scoreLabel = computed(() =>
  t('reports.table.percentileScore', {
    value: format.formatNumber(Number(rawValue.value), { maxFrac: 0 }),
  }),
)
</script>

<template>
  <span
    v-if="band"
    class="report-percentile-cell inline-flex whitespace-nowrap rounded-sm px-xs py-2xs text-2xs font-semibold"
    :class="BAND_CLASSES[band]"
    :data-benchmark-band="band"
  >
    {{ scoreLabel }}
  </span>
  <span v-else class="text-muted text-xs">{{ t('reports.table.cellUnavailable') }}</span>
</template>
